/**
 * <ec-high-variant-picker>
 *
 * Light-DOM Web Component driving variant selection on high-variant PDPs
 * (product.variants_count > 250), where Liquid's 250-entry cap on
 * `product.variants` makes the client-side map <ec-variant-picker> relies on
 * both truncated and huge. Nothing is resolved here: on every pick the
 * component re-requests its own section through the Section Rendering API
 * with `?option_values=<id,…>` and adopts what Shopify computed.
 *
 * Owns:
 *   - the round trip (aborting the previous one, so a fast clicker never
 *     applies a stale answer)
 *   - adopting the returned option grid, price, stock and variant id
 *   - history: pushState per pick, popstate replays from the URL
 *   - a polite live region + focus restoration across the innerHTML swap
 *
 * Server-side counterparts: snippets/ec-high-variant-picker.liquid (markup)
 * and sections/ec-product.liquid (which decides this component renders at all).
 *
 * Deliberately NOT a subclass of, nor a mode of, <ec-variant-picker>: below
 * the cap that component keeps its exact current behaviour, zero shared code
 * path, zero regression surface on the ~627 products of the catalogue.
 */

/** data-* attributes carrying the state the server just resolved. */
const STATE_ATTRS = [
  'data-product-url',
  'data-variant-id',
  'data-variant-available',
  'data-variant-image',
  'data-announce',
];

class EcHighVariantPicker extends HTMLElement {
  #abort = null;
  #onChange = null;
  #onPopState = null;

  connectedCallback() {
    this.#onChange = (event) => this.#handleChange(event);
    this.addEventListener('change', this.#onChange);

    this.#onPopState = (event) => this.#handlePopState(event);
    window.addEventListener('popstate', this.#onPopState);

    // Stamp the entry so a later back() lands on a history entry this
    // component recognises as its own — without it, the first Back after a
    // pick hits a null state and nothing re-renders.
    this.#writeHistory('replace', null, null);

    // The server already rendered the right CTA state; this only covers the
    // hidden `id` input, which must be disabled when no variant exists.
    this.#syncForm();
  }

  disconnectedCallback() {
    if (this.#onChange) this.removeEventListener('change', this.#onChange);
    if (this.#onPopState) window.removeEventListener('popstate', this.#onPopState);
    if (this.#abort) this.#abort.abort();
  }

  #handleChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.matches('[data-ec-option-input]')) return;

    // Combined listing: the value belongs to another product, so there is no
    // partial update that would leave the page coherent (title, gallery,
    // description all change). Hand over to the browser.
    const foreignUrl = target.dataset.ecProductUrl;
    if (foreignUrl && foreignUrl !== this.dataset.productUrl) {
      window.location.assign(
        this.#sectionEndpoint(foreignUrl, this.#checkedOptionValueIds(), false)
      );
      return;
    }

    this.#render({
      productUrl: this.dataset.productUrl || window.location.pathname,
      optionValues: this.#checkedOptionValueIds(),
      focusId: target.id,
      // 'replace', like <ec-variant-picker>: a pick is not a navigation. On a
      // 3-axis pack the customer tries a dozen combinations, and pushing each
      // one would bury the collection page a dozen Back presses deep. The URL
      // still updates, so deep links and reloads keep working.
      history: 'replace',
    });
  }

  #handlePopState(event) {
    if (!event.state || !event.state.ecHighVariant) return;
    const raw = new URLSearchParams(window.location.search).get('option_values') || '';
    this.#render({
      productUrl: window.location.pathname,
      optionValues: raw.split(',').filter(Boolean),
      focusId: null,
      // The address bar already holds the target: touching history here would
      // append the entry we are travelling back through.
      history: 'none',
    });
  }

  /**
   * Checked option value ids, in document order — which is option order,
   * as the API requires one id per option in the product's own order.
   */
  #checkedOptionValueIds() {
    return Array.from(this.querySelectorAll('[data-ec-option-input]:checked'))
      .map((input) => input.dataset.ecOptionValueId)
      .filter(Boolean);
  }

  /**
   * Carries the current query string over. Theme previews hang the theme id
   * (`preview_theme_id`) and the storefront session on it: a section request
   * built from the bare product path renders the PUBLISHED theme, or 404s —
   * which makes every unpublished-theme test a false negative.
   */
  #sectionEndpoint(productUrl, optionValues, withSectionId = true) {
    const url = new URL(productUrl, window.location.origin);
    new URLSearchParams(window.location.search).forEach((value, key) => {
      if (key === 'section_id' || key === 'option_values' || key === 'variant') return;
      url.searchParams.set(key, value);
    });
    if (withSectionId) url.searchParams.set('section_id', this.dataset.sectionId || '');
    if (optionValues.length > 0) url.searchParams.set('option_values', optionValues.join(','));
    return url.toString();
  }

  async #render({ productUrl, optionValues, focusId, history }) {
    if (!this.dataset.sectionId) return;

    if (this.#abort) this.#abort.abort();
    const controller = new AbortController();
    this.#abort = controller;
    this.#setLoading(true);

    try {
      const response = await fetch(this.#sectionEndpoint(productUrl, optionValues), {
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = doc.querySelector('ec-high-variant-picker');
      if (!fresh) throw new Error('rendered section carries no picker');

      this.#adopt(fresh);
      this.#swapRegion(doc, '[data-ec-product-price]');
      this.#swapRegion(doc, '[data-ec-product-stock]');
      this.#syncForm();
      this.#syncGallery();
      this.#writeHistory(history, productUrl, optionValues);
      this.#announce();
      this.#restoreFocus(focusId);
      this.#dispatchUpdate();
    } catch (err) {
      // An abort means a newer pick took over and already owns the loading
      // state; bailing out here leaves it to finish.
      if (err && err.name === 'AbortError') return;
      console.error('[ec-high-variant-picker] section render failed', err);
      // The clicked radio is checked but price, stock and the hidden `id` still
      // describe the previous combination — the one invariant that must never
      // break is that the form cannot order something the page isn't showing.
      // Lock the CTA and say so; the next click retries.
      this.#failClosed();
    } finally {
      if (this.#abort === controller) {
        this.#abort = null;
        this.#setLoading(false);
      }
    }
  }

  /**
   * Takes over the freshly rendered picker. innerHTML rather than replacing
   * the element: this instance keeps its listeners and its in-flight
   * controller, and the customer's next click is never dropped on a node
   * that no longer exists.
   */
  #adopt(fresh) {
    STATE_ATTRS.forEach((attr) => {
      const next = fresh.getAttribute(attr);
      if (next === null) this.removeAttribute(attr);
      else this.setAttribute(attr, next);
    });
    this.innerHTML = fresh.innerHTML;
  }

  #swapRegion(doc, selector) {
    const fresh = doc.querySelector(selector);
    const current = document.querySelector(selector);
    if (!fresh || !current) return;
    current.replaceWith(fresh);
  }

  #syncForm() {
    const formId = this.dataset.formId;
    if (!formId) return;
    const form = document.getElementById(formId);
    if (!(form instanceof HTMLFormElement)) return;

    const variantId = this.dataset.variantId || '';
    const available = variantId !== '' && this.dataset.variantAvailable === 'true';

    const input = form.querySelector('[data-ec-variant-id]');
    if (input instanceof HTMLInputElement) {
      input.value = variantId;
      // No variant behind this combination: keep `id` out of the payload
      // entirely rather than posting an empty one to /cart/add.js.
      input.disabled = variantId === '';
    }

    const cta = form.querySelector('[data-ec-add-to-cart]');
    const soldout = form.querySelector('[data-ec-soldout]');
    if (cta instanceof HTMLElement) cta.hidden = !available;
    if (soldout instanceof HTMLElement) soldout.hidden = available;
  }

  /**
   * Same two-step as <ec-variant-picker>: paint the variant image directly,
   * then let <ec-product-gallery> realign its thumbs off the update event.
   */
  #syncGallery() {
    const src = this.dataset.variantImage;
    if (!src) return;
    const mainImg = document.querySelector('.ec-product-gallery__main-img');
    if (mainImg instanceof HTMLImageElement) {
      mainImg.src = src;
      mainImg.removeAttribute('srcset');
    }
  }

  #writeHistory(mode, productUrl, optionValues) {
    if (mode === 'none') return;
    const state = Object.assign({}, window.history.state, { ecHighVariant: true });

    if (mode === 'replace' && productUrl === null) {
      window.history.replaceState(state, '');
      return;
    }

    // Built from the current location, not from productUrl alone: everything
    // else already in the query (theme preview, campaign params) has to
    // survive a pick.
    const url = new URL(window.location.href);
    url.pathname = new URL(productUrl, window.location.origin).pathname;
    // `option_values` is the canonical high-variant deep link. A leftover
    // `?variant=` would describe a different combination, and on reload the
    // two would fight over what the server selects.
    url.searchParams.delete('variant');
    if (optionValues.length > 0) url.searchParams.set('option_values', optionValues.join(','));
    else url.searchParams.delete('option_values');

    if (mode === 'push') window.history.pushState(state, '', url.toString());
    else window.history.replaceState(state, '', url.toString());
  }

  /** Round trip lost: no variant id, no purchase, and the customer is told. */
  #failClosed() {
    this.dataset.variantId = '';
    this.dataset.variantAvailable = 'false';
    this.#syncForm();
    this.#announce(this.dataset.i18nError || '');
  }

  #announce(override) {
    const status = document.querySelector('[data-ec-hv-status]');
    const text = override === undefined ? this.dataset.announce || '' : override;
    if (status instanceof HTMLElement && text) status.textContent = text;
  }

  /**
   * Input ids are derived from section.id + option/value ranks server-side, so
   * the node the customer was on survives the swap under the same id.
   */
  #restoreFocus(focusId) {
    if (!focusId) return;
    const input = document.getElementById(focusId);
    if (input instanceof HTMLElement) input.focus({ preventScroll: true });
  }

  #setLoading(isLoading) {
    this.toggleAttribute('data-ec-loading', isLoading);
    this.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  /** Same contract as <ec-variant-picker>, so existing subscribers apply. */
  #dispatchUpdate() {
    const id = this.dataset.variantId || '';
    if (!id) return;
    this.dispatchEvent(
      new CustomEvent('ec:variant:update', {
        bubbles: true,
        detail: {
          variant: {
            id: Number(id),
            available: this.dataset.variantAvailable === 'true',
            featured_image: this.dataset.variantImage || null,
          },
        },
      })
    );
  }
}

if (!customElements.get('ec-high-variant-picker')) {
  customElements.define('ec-high-variant-picker', EcHighVariantPicker);
}
